import type { Box, BuddhaMask } from '../buddha';

/** 前景光（叠在佛像之上，CSS 以 screen 混合）：只放柔和、低强度、保留五官衣饰细节的光 */
export interface FrontFrame {
  box: Box;
  /** 身内透光（佛身自身的柔和金光） */
  body: { i: number; color: string };
  /** 内缘光：光从身后漫过剪影边缘 */
  rim: { i: number; color: string };
  /** 眉间白毫柔光 */
  urna: { x: number; y: number; r: number; i: number; color: string; pulse: number };
  /** 佛顶（肉髻）柔光 */
  crown: { x: number; y: number; r: number; i: number; color: string };
}

export class BuddhaFront {
  private ctx: CanvasRenderingContext2D;
  private mask: BuddhaMask | null = null;
  private tinted = new Map<string, HTMLCanvasElement>();
  private dpr = 1;

  constructor(private readonly canvas: HTMLCanvasElement) {
    this.ctx = canvas.getContext('2d')!;
  }

  setMask(mask: BuddhaMask): void {
    this.mask = mask;
    this.tinted.clear();
  }

  resize(w: number, h: number): void {
    this.dpr = Math.min(window.devicePixelRatio || 1, 2);
    const cw = Math.round(w * this.dpr);
    const ch = Math.round(h * this.dpr);
    if (this.canvas.width !== cw || this.canvas.height !== ch) {
      this.canvas.width = cw;
      this.canvas.height = ch;
    }
  }

  private tint(src: HTMLCanvasElement, key: string, color: string): HTMLCanvasElement {
    const k = key + color;
    let c = this.tinted.get(k);
    if (!c) {
      c = document.createElement('canvas');
      c.width = src.width;
      c.height = src.height;
      const x = c.getContext('2d')!;
      x.drawImage(src, 0, 0);
      x.globalCompositeOperation = 'source-in';
      x.fillStyle = color;
      x.fillRect(0, 0, c.width, c.height);
      this.tinted.set(k, c);
      if (this.tinted.size > 16) this.tinted.delete(this.tinted.keys().next().value!);
    }
    return c;
  }

  draw(f: FrontFrame): void {
    const ctx = this.ctx;
    const d = this.dpr;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    ctx.setTransform(d, 0, 0, d, 0, 0);
    ctx.globalCompositeOperation = 'lighter';
    const { box } = f;
    if (this.mask) {
      if (f.body.i > 0.002) {
        ctx.globalAlpha = Math.min(1, f.body.i);
        ctx.drawImage(this.tint(this.mask.bodyCanvas, 'body', f.body.color), box.x, box.y, box.w, box.h);
      }
      if (f.rim.i > 0.002) {
        ctx.globalAlpha = Math.min(1, f.rim.i);
        ctx.drawImage(this.tint(this.mask.rimCanvas, 'rim', f.rim.color), box.x, box.y, box.w, box.h);
      }
    }
    ctx.globalAlpha = 1;
    // 佛顶柔光：肉髻上的一层薄光，与身后佛顶光晕相接
    if (f.crown.i > 0.002) {
      const { x, y, r } = f.crown;
      const g = ctx.createRadialGradient(x, y, 0, x, y, r);
      const a = Math.min(0.3, f.crown.i * 0.14);
      g.addColorStop(0, rgba(f.crown.color, a));
      g.addColorStop(0.5, rgba(f.crown.color, a * 0.35));
      g.addColorStop(1, rgba(f.crown.color, 0));
      ctx.fillStyle = g;
      ctx.fillRect(x - r, y - r, 2 * r, 2 * r);
    }
    // 眉间白毫：清晰端正的白毫珠 + 柔和光辉（不做定向光束）
    if (f.urna.i > 0.002) {
      const { x, y, r, i } = f.urna;
      const glowR = r * (7 + 2 * f.urna.pulse);
      const g = ctx.createRadialGradient(x, y, 0, x, y, glowR);
      const a = Math.min(0.95, i * 0.85);
      g.addColorStop(0, rgba(f.urna.color, a));
      g.addColorStop(0.1, rgba(f.urna.color, a * 0.8));
      g.addColorStop(0.28, rgba(f.urna.color, a * 0.3));
      g.addColorStop(0.6, rgba(f.urna.color, a * 0.08));
      g.addColorStop(1, rgba(f.urna.color, 0));
      ctx.fillStyle = g;
      ctx.fillRect(x - glowR, y - glowR, glowR * 2, glowR * 2);
      // 白毫珠本体：一颗柔亮的小珠，轮廓端正
      const core = ctx.createRadialGradient(x - r * 0.25, y - r * 0.25, 0, x, y, r);
      core.addColorStop(0, rgba('#ffffff', Math.min(0.9, 0.35 + i * 0.4)));
      core.addColorStop(0.7, rgba(f.urna.color, Math.min(0.6, 0.2 + i * 0.3)));
      core.addColorStop(1, rgba(f.urna.color, 0));
      ctx.fillStyle = core;
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalCompositeOperation = 'source-over';
  }
}

function rgba(hex: string, a: number): string {
  const n = parseInt(hex.replace('#', '').slice(0, 6), 16) || 0;
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${Math.max(0, Math.min(1, a)).toFixed(3)})`;
}
